import { ApiError } from "@sidegigllc/api/resources";

/** External dynalinks API — auth header is injected server-side by the nginx/Vite proxy. */
const BASE_URL = "/dynalinks-api";

async function request<T>(
  method: string,
  path: string,
  init?: { params?: Record<string, string | number | null | undefined>; body?: unknown },
) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(init?.params ?? {})) {
    if (value !== undefined && value !== null) search.append(key, String(value));
  }
  const query = search.size > 0 ? `?${search}` : "";
  const url = `${BASE_URL}${path}${query}`;

  const response = await fetch(url, {
    method,
    ...(init?.body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(init.body),
    }),
  });

  if (response.status === 204) {
    if (!response.ok) throw ApiError.fromResponseData(undefined, response.status, method, url);
    return undefined as T;
  }

  const isJson = (response.headers.get("content-type") ?? "").includes("application/json");
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) throw ApiError.fromResponseData(data, response.status, method, url);

  return data as T;
}

export type DynamicLink = {
  id: number;
  name: string;
  path: string;
  url: string;
  social_title: string | null;
  social_description: string | null;
  social_image_url: string | null;
  ios_fallback_url: string | null;
  android_fallback_url: string | null;
  enable_forced_redirect: boolean;
  ios_deferred_deep_linking_enabled: boolean;
  clicks: number;
  created_at: string;
  updated_at: string;
};

export type CreateDynamicLinkPayload = {
  name: string;
  path: string;
  url: string;
  social_title?: string;
  social_description?: string;
  social_image_url?: string;
  ios_fallback_url?: string;
  android_fallback_url?: string;
  enable_forced_redirect?: boolean;
  ios_deferred_deep_linking_enabled?: boolean;
};

export type DynamicLinksListParams = {
  limit?: number;
  page?: number;
  "q[path_eq]"?: string;
};

export type DynamicLinksListResponse = {
  links: Array<DynamicLink>;
  meta: {
    page: number;
    count: number;
    limit: number;
  };
};

export const dynamicLinksClient = {
  list: (params: DynamicLinksListParams) => request<DynamicLinksListResponse>("GET", "/api/v1/links", { params }),

  create: (payload: CreateDynamicLinkPayload) => request<DynamicLink>("POST", "/api/v1/links", { body: payload }),

  update: (id: number, payload: Partial<CreateDynamicLinkPayload>) =>
    request<DynamicLink>("PATCH", `/api/v1/links/${id}`, { body: payload }),

  // oxlint-disable-next-line typescript/no-invalid-void-type -- void is the response type of the empty DELETE body
  delete: (id: number) => request<void>("DELETE", `/api/v1/links/${id}`),
};
